// /stripe — Stripe-sync visibility & remediation. Four tabs:
//   • Webhooks    — list processed_webhook_events; retry stuck/rejected
//   • Meter pusher — unflushed-count + last-flush + manual flush trigger
//   • Reconciliation — link to /billing recon view + manual trigger
//   • Divergence  — open subscription_divergence + missing-customer

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Play, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient } from '../../lib/providers.js';
import {
  STRIPE_DIVERGENCE_QUERY_KEY,
  STRIPE_METER_PUSHER_QUERY_KEY,
  STRIPE_MISSING_QUERY_KEY,
  STRIPE_WEBHOOKS_QUERY_KEY,
  stripeApi,
  webhookBadgeVariant,
  type DivergenceRow,
  type MeterPusherState,
  type MissingSubRow,
  type WebhookRow,
  type WebhookStatusFilter,
} from '../../lib/api/stripe.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatDateTime } from '../../lib/format-date.js';

export function StripePage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Stripe sync"
        description="Webhook deliveries, meter-pusher state, reconciliation runs, and subscription-state divergence — across every tenant."
      />

      <Tabs defaultValue="webhooks">
        <TabsList>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="meter-pusher">Meter pusher</TabsTrigger>
          <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
          <TabsTrigger value="divergence">Divergence</TabsTrigger>
        </TabsList>
        <TabsContent value="webhooks" className="mt-6">
          <WebhooksTab />
        </TabsContent>
        <TabsContent value="meter-pusher" className="mt-6">
          <MeterPusherTab />
        </TabsContent>
        <TabsContent value="reconciliation" className="mt-6">
          <ReconciliationTab />
        </TabsContent>
        <TabsContent value="divergence" className="mt-6">
          <DivergenceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Webhooks tab ─────────────────────────────────────────────────

function WebhooksTab() {
  const client = useApiClient();
  const [status, setStatus] = useState<WebhookStatusFilter>('all');
  const query = useQuery({
    queryKey: [...STRIPE_WEBHOOKS_QUERY_KEY, status] as const,
    queryFn: () => stripeApi.listWebhooks(client, { status, sinceHours: 168, limit: 200 }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5 sm:w-48">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as WebhookStatusFilter);
            }}
          >
            <option value="all">All</option>
            <option value="received">Received (open)</option>
            <option value="processed">Processed</option>
            <option value="rejected">Rejected</option>
            <option value="skipped">Skipped</option>
            <option value="stuck">Stuck (received &gt; 1h)</option>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="px-0 pb-0">
          {query.isPending ? (
            <SkeletonTable />
          ) : query.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load webhooks"
                error={query.error}
                onRetry={() => {
                  void query.refetch();
                }}
              />
            </div>
          ) : query.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No webhooks match"
                description="Try widening the status filter or letting more time pass."
              />
            </div>
          ) : (
            <WebhookTable rows={query.data.rows} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WebhookTable({ rows }: { rows: readonly WebhookRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Received</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.source}-${row.eventId}`}>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
              {formatDateTime(row.receivedAt)}
            </TableCell>
            <TableCell className="text-xs">{row.source}</TableCell>
            <TableCell>
              <div className="font-mono text-xs">{row.eventType}</div>
              <code className="text-[10px] text-muted-foreground">{row.eventId}</code>
            </TableCell>
            <TableCell>
              {row.tenantId ? (
                <Link
                  to={`/tenants/${row.tenantId}`}
                  className="hover:text-foreground"
                >
                  {row.tenantName ?? row.tenantId.slice(0, 8)}
                </Link>
              ) : (
                <span className="text-xs italic text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={webhookBadgeVariant(row.status, row.stuck)}>
                {row.stuck ? 'stuck' : row.status}
              </Badge>
              {row.error && (
                <div
                  className="mt-1 max-w-xs truncate text-[10px] text-destructive"
                  title={row.error}
                >
                  {row.error}
                </div>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="inline-flex gap-2">
                <DetailDialog row={row} />
                {(row.status === 'rejected' || row.stuck) && <RetryButton row={row} />}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DetailDialog({ row }: { row: WebhookRow }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        View
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row.source} · {row.eventType}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <KV k="Event id" v={row.eventId} />
          <KV k="Status" v={row.status} />
          <KV k="Received" v={formatDateTime(row.receivedAt)} />
          <KV k="Processed" v={row.processedAt ? formatDateTime(row.processedAt) : '—'} />
          <KV k="Tenant" v={row.tenantId ?? '—'} />
          {row.error && (
            <div>
              <Label className="text-xs text-muted-foreground">Error</Label>
              <pre className="mt-1 max-h-48 overflow-auto rounded-md border bg-destructive/5 p-3 text-xs text-destructive">
                {row.error}
              </pre>
            </div>
          )}
          <a
            href={`https://dashboard.stripe.com/events/${row.eventId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink aria-hidden className="h-3 w-3" />
            Open in Stripe Dashboard
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RetryButton({ row }: { row: WebhookRow }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => stripeApi.retryWebhook(client, row.source, row.eventId),
    onSuccess: (result) => {
      toast.success(`Retry ${result.disposition}`);
      void qc.invalidateQueries({ queryKey: STRIPE_WEBHOOKS_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Retry failed');
    },
  });
  return (
    <Button
      variant="default"
      size="sm"
      disabled={mut.isPending}
      onClick={() => {
        mut.mutate();
      }}
    >
      <RefreshCw aria-hidden className="mr-1 h-3.5 w-3.5" />
      {mut.isPending ? 'Retrying…' : 'Retry'}
    </Button>
  );
}

// ─── Meter pusher tab ─────────────────────────────────────────────

function MeterPusherTab() {
  const client = useApiClient();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: STRIPE_METER_PUSHER_QUERY_KEY,
    queryFn: () => stripeApi.meterPusher(client),
  });
  const flushMut = useMutation({
    mutationFn: () => stripeApi.flushMeterPusher(client),
    onSuccess: (r) => {
      toast.success(`Flushed ${String(r.pushedCount)} rows`);
      void qc.invalidateQueries({ queryKey: STRIPE_METER_PUSHER_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Flush failed');
    },
  });

  if (query.isPending) return <Skeleton className="h-32 w-full" />;
  if (query.isError) {
    return (
      <ErrorRetry
        title="Failed to load meter-pusher state"
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const state: MeterPusherState = query.data;

  return (
    <div className="space-y-4">
      {!state.pusherWired && (
        <DeferredBanner text="Meter pusher singleton not wired in this environment. Manual flush is unavailable; the unflushed-count + last-flush timestamps still surface so staff can spot a backlog." />
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          label="Unflushed rows"
          value={state.unflushedCount.toLocaleString()}
          hint={
            state.unflushedCount === 0
              ? 'Healthy'
              : state.oldestUnflushedTs
                ? `Oldest: ${formatDateTime(state.oldestUnflushedTs)}`
                : ''
          }
          severity={state.unflushedCount > 100 ? 'destructive' : 'default'}
        />
        <SummaryCard
          label="Last flush"
          value={state.lastFlushedAt ? formatDateTime(state.lastFlushedAt) : 'Never'}
          hint={state.lastFlushedAt ? '' : 'No rows flushed yet (cold start or Stripe disabled)'}
        />
        <Card>
          <CardContent className="space-y-3 py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Manual flush
            </div>
            <Button
              size="sm"
              disabled={!state.pusherWired || flushMut.isPending}
              onClick={() => {
                flushMut.mutate();
              }}
            >
              <Send aria-hidden className="mr-1 h-3.5 w-3.5" />
              {flushMut.isPending ? 'Flushing…' : 'Trigger flush now'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Reconciliation tab ───────────────────────────────────────────

function ReconciliationTab() {
  const client = useApiClient();
  const reconMut = useMutation({
    mutationFn: () => stripeApi.runReconciliation(client),
    onSuccess: (r) => {
      toast.success(
        `Recon ran: green=${String(r.green)} warn=${String(r.driftWarn)} alert=${String(r.driftAlert)}`,
      );
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Recon run failed');
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Manual reconciliation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Triggers a recon pass for the previous UTC day across every tenant.
            Requires the recon job singleton — when absent (e.g. dev w/o
            Stripe), this returns 503.
          </p>
          <Button
            disabled={reconMut.isPending}
            onClick={() => {
              reconMut.mutate();
            }}
          >
            <Play aria-hidden className="mr-1 h-3.5 w-3.5" />
            {reconMut.isPending ? 'Running…' : 'Run reconciliation now'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent runs</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            The last 30 reconciliation runs across every tenant live on{' '}
            <Link to="/billing" className="underline hover:text-foreground">
              /billing
            </Link>
            . Future P5+ work will surface a day-tile heatmap directly here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Divergence tab ───────────────────────────────────────────────

function DivergenceTab() {
  const client = useApiClient();
  const divQuery = useQuery({
    queryKey: STRIPE_DIVERGENCE_QUERY_KEY,
    queryFn: () => stripeApi.listDivergence(client, { onlyOpen: true }),
  });
  const missingQuery = useQuery({
    queryKey: STRIPE_MISSING_QUERY_KEY,
    queryFn: () => stripeApi.listMissingCustomer(client, { limit: 100 }),
  });

  return (
    <div className="space-y-6">
      <DeferredBanner text="The detection cron that populates subscription_divergence lands in a follow-up — see admin-dashboard-plan-v2 §11. The list will populate once the job is wired." />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open divergence rows</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {divQuery.isPending ? (
            <SkeletonTable />
          ) : divQuery.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load divergence"
                error={divQuery.error}
                onRetry={() => {
                  void divQuery.refetch();
                }}
              />
            </div>
          ) : divQuery.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No open divergence"
                description="Either the detector is idle (no cron yet) or every detected mismatch is already resolved."
              />
            </div>
          ) : (
            <DivergenceTable rows={divQuery.data.rows} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Missing Stripe customer / subscription</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {missingQuery.isPending ? (
            <SkeletonTable />
          ) : missingQuery.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load missing-customer list"
                error={missingQuery.error}
                onRetry={() => {
                  void missingQuery.refetch();
                }}
              />
            </div>
          ) : missingQuery.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState
                title="No missing customers"
                description="Every tenant has a Stripe customer + subscription mapping."
              />
            </div>
          ) : (
            <MissingTable rows={missingQuery.data.rows} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DivergenceTable({ rows }: { rows: readonly DivergenceRow[] }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const resolveMut = useMutation({
    mutationFn: (id: string) => stripeApi.resolveDivergence(client, id),
    onSuccess: () => {
      toast.success('Divergence resolved');
      void qc.invalidateQueries({ queryKey: STRIPE_DIVERGENCE_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Resolve failed');
    },
  });
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Detected</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Local</TableHead>
          <TableHead>Stripe</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
              {formatDateTime(r.detectedAt)}
            </TableCell>
            <TableCell>
              <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                {r.tenantName}
              </Link>
            </TableCell>
            <TableCell>
              <Badge variant="secondary">{r.localStatus}</Badge>
            </TableCell>
            <TableCell>
              <Badge variant="destructive">{r.stripeStatus}</Badge>
            </TableCell>
            <TableCell className="text-right">
              <Button
                size="sm"
                variant="outline"
                disabled={resolveMut.isPending}
                onClick={() => {
                  resolveMut.mutate(r.id);
                }}
              >
                <CheckCircle2 aria-hidden className="mr-1 h-3.5 w-3.5" />
                Mark resolved
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MissingTable({ rows }: { rows: readonly MissingSubRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Missing</TableHead>
          <TableHead>Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.tenantId}>
            <TableCell>
              <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                {r.tenantName}
              </Link>
            </TableCell>
            <TableCell>
              <Badge variant={r.missing === 'customer' ? 'destructive' : 'secondary'}>
                {r.missing === 'customer' ? 'Stripe customer' : 'Stripe subscription'}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDateTime(r.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  hint,
  severity = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  severity?: 'default' | 'destructive';
}) {
  return (
    <Card className={severity === 'destructive' ? 'border-destructive/40' : ''}>
      <CardContent className="space-y-1 py-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div
          className={`text-2xl font-semibold tabular-nums ${
            severity === 'destructive' ? 'text-destructive' : ''
          }`}
        >
          {value}
        </div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="truncate text-right">{v}</span>
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="space-y-2 px-4 py-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function DeferredBanner({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
      <strong>Partial signal:</strong> {text}
    </div>
  );
}
